import {
  portalSkin,
  type PortalAppearance,
  type PortalThemeRoles,
} from "@shared/consumer-tvod";

/**
 * Paint the portal in the provider's brand.
 *
 * The server resolves the provider's `brand` through
 * `@shared/schemes/supplierTheme` - the same resolver the CMS supplier shell
 * uses - and sends the result as `skin.theme` on `GET /api/interactiveplayer/v1/portal/context`.
 * That resolver already enforces the readability floor (an unreadable ink on
 * the provider's ground is replaced, never applied) and keeps `danger` as
 * Foyer's own, so the delete-account button cannot be re-branded into
 * something friendly.
 *
 * `portalSkin` (shared with the CMS Appearance preview, so the preview cannot
 * promise a look the portal does not paint) turns those roles plus the
 * portal's `appearance` - type pairing, corners, density - into custom
 * properties, adding the derived roles a stylesheet needs to stay readable:
 * `--accent-ink` (the accent as text, deepened when it is too pale for
 * small type), `--focus`, and `rgb` triplets for scrims. This file only
 * writes them onto the document; `styles.css` reads roles, never colours.
 */

/** Every property `portalSkin` can write - cleared when a role is absent. */
const PROPS = [
  "--paper",
  "--surface",
  "--surface-alt",
  "--ink",
  "--ink-soft",
  "--ink-muted",
  "--hairline",
  "--accent",
  "--accent-deep",
  "--accent-tint",
  "--on-accent",
  "--danger",
  "--on-danger",
  "--sans",
  "--display",
  "--mono",
  "--accent-ink",
  "--focus",
  "--paper-rgb",
  "--ink-rgb",
  "--accent-rgb",
  "--display-weight",
  "--display-tracking",
  "--display-scale",
  "--radius",
  "--radius-sm",
  "--radius-lg",
  "--radius-pill",
  "--card-w",
  "--grid-min",
  "--gap",
] as const;

export function applySkin(
  theme: PortalThemeRoles | null | undefined,
  appearance?: PortalAppearance | null
): void {
  const root = document.documentElement;
  const skin = portalSkin(theme ?? null, appearance ?? null);
  for (const prop of PROPS) {
    const value = skin.vars[prop];
    // A role the theme omits keeps the stylesheet default rather than an
    // empty property, which would cascade to `initial`.
    if (typeof value === "string" && value.trim()) {
      root.style.setProperty(prop, value);
    } else {
      root.style.removeProperty(prop);
    }
  }
  if (theme) {
    root.style.setProperty("color-scheme", skin.colorScheme);
    root.dataset.scheme = skin.colorScheme;
  } else {
    root.style.removeProperty("color-scheme");
    delete root.dataset.scheme;
  }
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (meta) {
    meta.setAttribute(
      "content",
      theme?.paper || meta.dataset.default || "#F6F6F4"
    );
  }
}

/**
 * Point the tab icon at the portal's favicon, else the provider's logo;
 * with neither it stays Foyer's neutral icon.
 */
export function applyIcon(
  logoUrl: string | null | undefined,
  faviconUrl?: string | null
): void {
  const href = faviconUrl || logoUrl;
  if (!href) return;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) {
    link.removeAttribute("type");
    link.href = href;
  }
}
